import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

// The script tag is also added in public/index.html, but this loader is a
// safe fallback (and de-dupes) in case it hasn't finished loading yet.
const loadTurnstile = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });

/**
 * Cloudflare Turnstile widget.
 * Calls onVerify(token) once solved; the token must be sent to the backend
 * and checked there against Cloudflare's siteverify API before the login/
 * register request is trusted. onExpire/onError clear out any stale token.
 */
// Cloudflare renders the widget at a fixed ~65px height regardless of `size`
// (there's no official "shorter" option) - `scale` visually shrinks it via
// CSS transform while keeping the click/checkbox area aligned.
const WIDGET_HEIGHT_PX = 65;

const Turnstile = forwardRef(function Turnstile(
  { siteKey, action, size = "flexible", scale = 1, onVerify, onExpire, onError },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      console.error("Turnstile: missing siteKey (REACT_APP_TURNSTILE_SITE_KEY not set)");
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || !turnstile) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          size,
          callback: (token) => onVerify && onVerify(token),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": () => onError && onError(),
        });
      })
      .catch((err) => {
        console.error("Failed to load Cloudflare Turnstile:", err);
        onError && onError();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, action, size]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  // "flexible" size fills the width of this container (Cloudflare stretches
  // the widget between ~300px and 800px); width: 100% lets it track the form.
  if (scale === 1) {
    return <div ref={containerRef} style={{ width: "100%" }} />;
  }

  // Reserve the scaled-down height so the shrunk widget doesn't leave a gap
  // below it, and scale from the top-left so it still spans the full width.
  return (
    <div style={{ height: WIDGET_HEIGHT_PX * scale, overflow: "hidden" }}>
      <div
        ref={containerRef}
        style={{
          width: `${100 / scale}%`,
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
        }}
      />
    </div>
  );
});

export default Turnstile;
